import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Image from '../../../components/AppImage';

const QuickMessageModal = ({ 
  isOpen, 
  onClose, 
  contact, 
  onSendMessage,
  messageTemplates = [],
  className = '' 
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [sendMethod, setSendMethod] = useState('email');
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [isSending, setIsSending] = useState(false);

  const sendMethodOptions = [
    { value: 'email', label: 'Email', icon: 'Mail' },
    { value: 'sms', label: 'SMS', icon: 'MessageSquare' },
    { value: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle' }
  ];

  const templateOptions = [
    { value: '', label: 'Custom Message' },
    ...messageTemplates?.map(template => ({
      value: template?.id,
      label: template?.name
    }))
  ];

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today?.getFullYear() - birth?.getFullYear();
    const monthDiff = today?.getMonth() - birth?.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birth?.getDate())) {
      age--;
    }
    
    return age + 1;
  };

  const getPersonalizedMessage = () => {
    if (!selectedTemplate) return customMessage;
    
    const template = messageTemplates?.find(t => t?.id === selectedTemplate);
    if (!template) return customMessage;
    
    return template?.content?.replace('{Name}', contact?.name || '')?.replace('{Age}', calculateAge(contact?.dateOfBirth || new Date())?.toString())?.replace('{Date}', new Date()?.toLocaleDateString());
  };

  const handleSend = async () => {
    if (!contact) return;
    
    setIsSending(true);
    
    const messageData = {
      contactId: contact?.id,
      message: getPersonalizedMessage(),
      method: sendMethod,
      isScheduled,
      scheduleDate: isScheduled ? `${scheduleDate} ${scheduleTime}` : null
    };
    
    try {
      await onSendMessage(messageData);
      onClose();
      resetForm();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const resetForm = () => {
    setSelectedTemplate('');
    setCustomMessage('');
    setSendMethod('email');
    setIsScheduled(false);
    setScheduleDate('');
    setScheduleTime('');
  };

  if (!isOpen || !contact) return null;

  return (
    <div className="fixed inset-0 z-500 flex items-center justify-center p-4 bg-black/50">
      <div className={`bg-surface rounded-lg shadow-modal max-w-lg w-full max-h-[90vh] overflow-y-auto ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
              {contact?.avatar ? (
                <Image
                  src={contact?.avatar}
                  alt={contact?.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="User" size={20} className="text-text-secondary" />
                </div>
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                Send Birthday Wish
              </h3>
              <p className="text-sm text-text-secondary">
                To {contact?.name} • Turning {calculateAge(contact?.dateOfBirth)}
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Send Method */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Send Method
            </label>
            <div className="grid grid-cols-3 gap-2">
              {sendMethodOptions?.map((method) => (
                <button
                  key={method?.value}
                  onClick={() => setSendMethod(method?.value)}
                  className={`flex flex-col items-center space-y-2 p-3 rounded-lg border transition-micro ${
                    sendMethod === method?.value
                      ? 'border-primary bg-primary/10 text-primary' :'border-border hover:border-primary/50'
                  }`}
                >
                  <Icon name={method?.icon} size={20} />
                  <span className="text-sm font-medium">{method?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Template Selection */}
          <div>
            <Select
              label="Message Template"
              options={templateOptions}
              value={selectedTemplate}
              onChange={setSelectedTemplate}
              placeholder="Choose a template or write custom message"
            />
          </div>

          {/* Message Content */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Message Preview
            </label>
            <textarea
              value={selectedTemplate ? getPersonalizedMessage() : customMessage}
              onChange={(e) => !selectedTemplate && setCustomMessage(e?.target?.value)}
              placeholder="Write your birthday message..."
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text-primary placeholder-text-secondary resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={!!selectedTemplate}
            />
            <p className="text-xs text-text-secondary mt-1">
              {selectedTemplate ? 'Template variables have been replaced automatically' : 'You can use {Name}, {Age}, {Date} variables'}
            </p>
          </div>

          {/* Schedule Options */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="schedule"
                checked={isScheduled}
                onChange={(e) => setIsScheduled(e?.target?.checked)}
                className="rounded border-border text-primary focus:ring-primary"
              />
              <label htmlFor="schedule" className="text-sm font-medium text-text-primary">
                Schedule for later
              </label>
            </div>

            {isScheduled && (
              <div className="grid grid-cols-2 gap-4 pl-6">
                <Input
                  type="date"
                  label="Date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e?.target?.value)}
                  min={new Date()?.toISOString()?.split('T')?.[0]}
                />
                <Input
                  type="time"
                  label="Time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e?.target?.value)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSend}
            loading={isSending}
            iconName={isScheduled ? "Clock" : "Send"}
            iconPosition="left"
            disabled={!getPersonalizedMessage()?.trim() || (isScheduled && (!scheduleDate || !scheduleTime))}
          >
            {isScheduled ? 'Schedule Message' : 'Send Now'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickMessageModal;